import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { PurposePicker, TagChips } from './EventTags';

/* The form a customer creates an event with — one copy, used everywhere an event is made under
   a container.

   Lifted out of CompanyProgramme, unchanged, when leagues needed the same thing. A league's
   master and a programme are both containers a day hangs under (``Event.master_event_id``), so
   they were about to have two forms that had to stay in step: the same fields, the same POST,
   and the same three rules below that took a while to get right the first time. One component,
   two screens.

   Deliberately short: a name, when, a line about it, its categories, and "repeat this".
   Everything else — where, online, the banner, who may see the numbers — lives in the editor,
   because it is not what you think about while you are still deciding to run the thing at all.

   ★ Repeating stamps out REAL sibling events sharing a series id. It is not a recurrence rule
   that the calendar, the tickets, the mails and the exports would each have to understand. */

export const REPEATS = ['weekly', 'biweekly', 'monthly'];

const HOUR_MS = 60 * 60 * 1000;

/* An hour after a datetime-local string, in the same shape the input speaks. Built back out of
   the local getters, so it is the wall clock that moves an hour — no timezone anywhere. */
export const anHourAfter = (local) => {
  const at = new Date(local);
  if (!local || Number.isNaN(at.getTime())) return '';
  at.setTime(at.getTime() + HOUR_MS);
  const pad = (n) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
         + `T${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

/* Picking a start fills the end an hour later, because that is what nearly every event is and
   filling the same date twice is the tedious half of this form. It only ever writes an end you
   did not choose: an empty one, one the new start has overtaken, or one still sitting exactly
   an hour after the previous start — that last case is how moving the start drags an untouched
   end along with it. A length you set yourself is never overwritten. */
export const withStart = (draft, date) => {
  const untouched = !draft.end_date
                    || draft.end_date <= date
                    || draft.end_date === anHourAfter(draft.date);
  return { ...draft, date, end_date: untouched ? anHourAfter(date) : draft.end_date };
};

export const emptyEventDraft = () => ({ name: '', date: '', end_date: '', short_desc: '',
                                        tags: [], purpose: { theme: [], value: [], sdg: [] },
                                        repeat: '', repeat_count: 4 });

/* The body ``POST /api/company/events`` wants, out of a draft and the container it hangs under.
   Here rather than at each call site, because the API names the three purpose lists after what
   they hold and not after the kind — which is exactly the sort of thing a second copy gets
   wrong. */
export const eventCreateBody = (draft, containerId, { timeZone } = {}) => {
  const { purpose, ...rest } = draft;
  const body = { ...rest, programme_id: containerId,
                 themes: purpose.theme, values: purpose.value, sdgs: purpose.sdg,
                 time_zone: timeZone || undefined };
  if (!body.repeat) { delete body.repeat; delete body.repeat_count; }
  else body.repeat_count = Number(body.repeat_count) || 0;
  return body;
};

/* "Repeat weekly" with a blank or 1 in the box would silently make ONE event — the picked
   frequency says otherwise, so the button refuses instead. */
export const eventDraftReady = (draft) => Boolean(
  draft && draft.name.trim() && draft.date
  && (!draft.repeat || Number(draft.repeat_count) >= 2));

const CompanyEventForm = ({ draft, setDraft, tags, purposeCatalog, busy, onSave, onCancel }) => {
  const { t } = useTranslation();
  if (!draft) return null;

  const toggleTag = (id) => setDraft((d) => ({
    ...d,
    tags: d.tags.includes(id) ? d.tags.filter((x) => x !== id) : [...d.tags, id],
  }));

  const togglePurpose = (kind, id) => setDraft((d) => {
    const on = d.purpose[kind] || [];
    return { ...d,
             purpose: { ...d.purpose,
                        [kind]: on.includes(id) ? on.filter((x) => x !== id) : [...on, id] } };
  });

  return (
    <div className="bravos-form">
      <input
        type="text"
        value={draft.name}
        maxLength={50}
        placeholder={t('eventName')}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
      />
      <div className="bravos-form__row">
        <label className="bravos-form__label">
          {t('startsAt')}
          <input
            type="datetime-local"
            value={draft.date}
            onChange={(e) => setDraft(withStart(draft, e.target.value))}
          />
        </label>
        <label className="bravos-form__label">
          {t('endsAt')}
          <input
            type="datetime-local"
            value={draft.end_date}
            onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
          />
        </label>
      </div>
      <textarea
        rows={2}
        maxLength={1024}
        value={draft.short_desc}
        placeholder={t('eventDescPlaceholder')}
        onChange={(e) => setDraft({ ...draft, short_desc: e.target.value })}
      />
      <span className="bravos-form__label">{t('eventTags')}</span>
      <TagChips tags={tags || []} selected={draft.tags} onToggle={toggleTag} />

      {/* What it is FOR, beside what KIND it is. Same three things the company already declares
          under Programmes, so this is a pick and never free text. */}
      <PurposePicker
        catalog={purposeCatalog}
        picks={draft.purpose}
        onToggle={togglePurpose}
        disabled={busy}
      />

      <div className="bravos-form__row">
        <label className="bravos-form__label">
          {t('repeat')}
          <select
            value={draft.repeat}
            onChange={(e) => setDraft({ ...draft, repeat: e.target.value })}
          >
            <option value="">{t('repeatNever')}</option>
            {REPEATS.map((r) => (
              <option key={r} value={r}>{t(`repeat_${r}`)}</option>
            ))}
          </select>
        </label>
        {draft.repeat && (
          <label className="bravos-form__label">
            {t('repeatCount')}
            <input
              type="number"
              min={2}
              max={24}
              /* ★ The RAW string, never Number(). Clearing the box gives '', and Number('') is
                 0 — so the field redrew as "0", and typing 10 into it produced "010". Coerced
                 once, on submit. */
              value={draft.repeat_count}
              onChange={(e) => setDraft({ ...draft, repeat_count: e.target.value })}
            />
          </label>
        )}
      </div>
      {draft.repeat && <p className="ce-note">{t('repeatNote')}</p>}

      <div className="bravos-form__row">
        <button
          type="button"
          className="bravos-submit"
          onClick={onSave}
          disabled={busy || !eventDraftReady(draft)}
        >
          {busy ? t('pleaseWait') : t('save')}
        </button>
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={onCancel}
        >
          {t('cancel')}
        </button>
      </div>
    </div>
  );
};

/* The "+ New event" button that opens it, so the two screens agree on that too. */
export const NewEventButton = ({ label, onClick }) => (
  <button type="button" className="bravos-submit bravos-submit--ghost" onClick={onClick}>
    <Icon name="plus" /> {label}
  </button>
);

export default CompanyEventForm;
